import { useNavigate } from 'react-router'
import { useAdminAuth } from '@/lib/admin/admin-context'
import { useAuth } from '@/lib/auth/auth-context'
import { LogOut, Shield, ArrowLeft } from 'lucide-react'

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { logout } = useAdminAuth()
  const { user, isImpersonating, endImpersonation } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = () => {
    logout()
    navigate('/admin')
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Impersonation banner */}
      {isImpersonating && user && (
        <div className="bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 flex items-center justify-between">
          <span>Impersonating <strong>{user.email}</strong>{user.admin ? " (Admin)" : ""}</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="underline hover:no-underline"
            >
              Open App
            </button>
            <button
              onClick={() => {
                endImpersonation()
                navigate('/admin/dashboard')
              }}
              className="bg-amber-950 text-amber-100 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-amber-900 transition-colors"
            >
              End Impersonation
            </button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-zinc-400" />
            <span className="font-bold tracking-tight text-lg" style={{ fontFamily: 'var(--font-syne)' }}>
              eT<span className="text-primary-500">X</span>
              <span className="text-zinc-300 mx-0.5 text-base font-light">/</span>
              <span className="text-zinc-500 font-medium text-base">Admin</span>
            </span>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            Internal
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            App
          </button>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {children}
      </main>
    </div>
  )
}
